import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Eye, EyeOff, Trash2 } from 'lucide-react';
import { getFavoriteQuestionDeleteUrl, getFavoriteQuestionUrl, getTagUrl } from '../utils/UrlUtilities';
import { useApiCaller } from '../utils/ApiUtilities';
import { getAnswerFromCorrectOption } from '../utils/BasicUtilities';
import { itemsPerPageOptions, TAGLEVELS } from '../utils/Constants';

const QUESTIONDIFFICULTY = ["EASY", "MEDIUM", "HARD"];


const FavoriteQuestionsGrid = () => {
    // Mock tags for dropdowns (using existing mockTags from previous context)
    const [allTags, setAllTags] = useState([]);
    const [allFavoriteQuestions, setAllFavoriteQuestions] = useState([]); // Holds all fetched data
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Filters state
    const [searchText, setSearchText] = useState('');
    const [selectedLevel, setSelectedLevel] = useState('');
    const [selectedSubject, setSelectedSubject] = useState('');
    const [selectedTopic, setSelectedTopic] = useState('');
    const [selectedExam, setSelectedExam] = useState('');
    const [selectedDifficulty, setSelectedDifficulty] = useState('');

    // Display options state
    const [showCorrectOption, setShowCorrectOption] = useState(false);
    const [showUserResult, setShowUserResult] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    
    // Selection for deletion state
    const [selectedRows, setSelectedRows] = useState([]);
    const CallApi = useApiCaller();


    const fetchTags = async () => {
        try {
            const tagUrl = getTagUrl();
            const tagGetResponse = await CallApi({
                url: tagUrl,
                method: 'GET',
                token: true,
            });
            if (tagGetResponse.success) {
                setAllTags(tagGetResponse.data.tags);
            } else {
                setAllTags([]);
            }
        } catch (error) {
            setAllTags([]);
        }
    };

    useEffect(() => {
        fetchTags();
    }, []);

    // Fetch ALL favorite questions once on component mount
    const fetchAllFavoriteQuestions = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const questionUrl = getFavoriteQuestionUrl();
            const questionGetResponse = await CallApi({
                url: questionUrl,
                method: 'GET',
                token: true,
            });
            // const questionGetResponse = await getFavoriteQuestionsApi(); // Call mock API to get all data
            if (questionGetResponse.success) {
                setAllFavoriteQuestions(
                    (questionGetResponse?.data?.favorites || []).map(f => ({
                        ...f.question,
                        favorite_id: f.id,
                        question_id: f.question_id,
                        marked_at: f.marked_at,
                    }))
                );
            } else {
                setError("Failed to fetch all favorite questions.");
                setAllFavoriteQuestions([]);
            }
        } catch (err) {
            setError("An error occurred while fetching all favorite questions.");
            setAllFavoriteQuestions([]);
        } finally {
            setLoading(false);
        }
    }, []); // Empty dependency array to run only once

    useEffect(() => {
        fetchAllFavoriteQuestions();
    }, []);


    // Apply filters and pagination whenever filter/pagination states or allFavoriteQuestions change
    const filteredAndPaginatedQuestions = useMemo(() => {
        let filtered = [...allFavoriteQuestions];

        if (searchText) {
            filtered = filtered.filter(q => q.question_text.toLowerCase().includes(searchText.toLowerCase()));
        }
        if (selectedLevel) {
            filtered = filtered.filter(q => q.level === selectedLevel);
        }
        if (selectedSubject) {
            filtered = filtered.filter(q => q.subject === selectedSubject);
        }
        if (selectedTopic) {
            filtered = filtered.filter(q => q.topic === selectedTopic);
        }
        if (selectedExam) {
            filtered = filtered.filter(q => q.exam === selectedExam);
        }
        if (selectedDifficulty) {
            filtered = filtered.filter(q => q.difficulty === selectedDifficulty);
        }

        // Reset page to 1 if filters change and current page becomes invalid
        const newTotalPages = Math.ceil(filtered.length / pageSize);
        if (currentPage > newTotalPages && newTotalPages > 0) {
            setCurrentPage(newTotalPages);
        } else if (filtered.length === 0 && currentPage !== 1) {
            setCurrentPage(1);
        }

        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return filtered.slice(startIndex, endIndex);

    }, [allFavoriteQuestions, searchText, selectedLevel, selectedSubject, selectedTopic, selectedExam, selectedDifficulty, currentPage, pageSize]);

    // Calculate total questions after filtering (for pagination display)
    const totalFilteredQuestions = useMemo(() => {
        let filtered = [...allFavoriteQuestions];

        if (searchText) {
            filtered = filtered.filter(q => q.question_text.toLowerCase().includes(searchText.toLowerCase()));
        }
        if (selectedLevel) {
            filtered = filtered.filter(q => q.level === selectedLevel);
        }
        if (selectedSubject) {
            filtered = filtered.filter(q => q.subject === selectedSubject);
        }
        if (selectedTopic) {
            filtered = filtered.filter(q => q.topic === selectedTopic);
        }
        if (selectedExam) {
            filtered = filtered.filter(q => q.exam === selectedExam);
        }
        if (selectedDifficulty) {
            filtered = filtered.filter(q => q.difficulty === selectedDifficulty);
        }
        return filtered.length;
    }, [allFavoriteQuestions, searchText, selectedLevel, selectedSubject, selectedTopic, selectedExam, selectedDifficulty]);

    const handleCheckboxChange = (id, event) => {
        if (event.target.checked) {
            if (!selectedRows || selectedRows.length === 0) {
                setSelectedRows([id]);
            } else {
                let newSelectedRows = [...selectedRows, id];
                setSelectedRows(newSelectedRows);
            }
        } else {
            let newSelectedRowsAfterRemoval = selectedRows.filter(rowId => rowId !== id);
            setSelectedRows(newSelectedRowsAfterRemoval);
        }
        console.log("selectedRows :: ", selectedRows);
    };

    // Handler for "Remove from Favorite" button
    const handleRemoveFromFavorite = async () => {
        if (selectedRows.length === 0) {
            alert("Please select at least one question to remove.");
            return;
        }
        try {
            const response = await CallApi({
                url: getFavoriteQuestionDeleteUrl(selectedRows),
                method: 'DELETE',
                token: true,
            });

            if (response.success) {
                setSelectedRows([]); // Clear selection
                // Re-fetch all data to update the base list for filtering/pagination
                fetchAllFavoriteQuestions();
            } else {
                alert(`Failed to remove questions: ${response.message || 'Unknown error'}`);
            }
        } catch (err) {
            alert("An error occurred during removal.");
            console.error("Remove from Favorite Error:", err);
        }
    };

    // Calculate total pages for pagination (based on filtered questions, not all)
    const totalPages = Math.ceil(totalFilteredQuestions / pageSize);

    // Handler for pagination buttons (circular navigation)
    const handlePageChange = (direction) => {
        setCurrentPage(prev => {
            if (direction === 'prev') {
                return prev > 1 ? prev - 1 : (totalPages === 0 ? 1 : totalPages); // Circular previous, handle 0 total pages
            } else {
                return prev < totalPages ? prev + 1 : 1; // Circular next, handle 0 total pages
            }
        });
    };

    // Render loading or error states
    if (loading) {
        return <div style={{ textAlign: 'center', padding: '2rem', color: '#4b5563' }}>Loading favorite questions...</div>;
    }

    if (error) {
        return <div style={{ textAlign: 'center', padding: '2rem', color: '#ef4444' }}>Error: {error}</div>;
    }

    return (
        <div style={{
            backgroundColor: '#ffffff',
            padding: '2rem',
            borderRadius: '0.75rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
            width: '95%', // Wider layout
            maxWidth: '1400px', // Even wider max-width
            margin: '2rem auto', // Center the component with margin
            minHeight: '70vh', // Minimum height for better appearance
            display: 'flex',
            flexDirection: 'column',
            // Removed overflowY: 'auto' from here to ensure only table body scrolls
        }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem', color: '#5b21b6', textAlign: 'center' }}>
                My Favorite Questions
            </h2>

            {/* Action Buttons and Filters Section */}
            <div style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Top Row: Display Options and Remove Button */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'space-between', alignItems: 'center' }}>
                    {/* Display Options */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <button
                            onClick={() => setShowCorrectOption(prev => !prev)}
                            style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid #ddd6fe', backgroundColor: showCorrectOption ? '#ddd6fe' : '#f9fafb', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                        >
                            {showCorrectOption
                                ? <><EyeOff style={{ width: '1rem', height: '1rem' }} /> Hide Correct Answer</>
                                : <><Eye style={{ width: '1rem', height: '1rem' }} /> Show Correct Answer</>}
                        </button>
                        <button
                            onClick={() => setShowUserResult(prev => !prev)}
                            style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid #ddd6fe', backgroundColor: showUserResult ? '#ddd6fe' : '#f9fafb', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                        >
                            {showUserResult
                                ? <><EyeOff style={{ width: '1rem', height: '1rem' }} /> Hide My Answer</>
                                : <><Eye style={{ width: '1rem', height: '1rem' }} /> Show My Answer</>}
                        </button>
                    </div>

                    {/* Remove from Favorite Button */}
                    <button
                        onClick={handleRemoveFromFavorite}
                        disabled={selectedRows.length === 0}
                        style={{
                            backgroundColor: selectedRows.length === 0 ? '#9ca3af' : '#dc2626',
                            color: '#ffffff',
                            padding: '0.6rem 1.2rem',
                            borderRadius: '0.5rem',
                            border: 'none',
                            cursor: selectedRows.length === 0 ? 'not-allowed' : 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                        }}
                    >
                        <Trash2 style={{ width: '1rem', height: '1rem' }} /> Remove from Favorite ({selectedRows.length})
                    </button>
                </div>

                {/* Second Row: Filters */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-end' }}>
                    <div style={{ flex: '1 1 200px' }}>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>Search Text</label>
                        <Input
                            type="text"
                            placeholder="Filter by question text"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #ddd6fe', borderRadius: '0.375rem' }}
                        />
                    </div>
                    <div style={{ flex: '1 1 150px' }}>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>Level</label>
                        <select
                            value={selectedLevel}
                            onChange={(e) => {
                                setSelectedLevel(e.target.value);
                                // Reset other filters when level changes
                                setSelectedSubject('');
                                setSelectedExam('');
                                setSelectedTopic('');
                            }}
                            style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #ddd6fe', borderRadius: '0.375rem', backgroundColor: '#ffffff' }}
                        >
                            <option value="">All Levels</option>
                            {TAGLEVELS.map(item => item.submenu
                                ? <optgroup key={item.value} label={item.label}>
                                      {item.submenu.map(sub => (
                                          <option key={sub.value} value={sub.value}>{sub.label}</option>
                                      ))}
                                  </optgroup>
                                : <option key={item.value} value={item.value}>{item.label}</option>
                            )}
                        </select>
                    </div>
                    <div style={{ flex: '1 1 150px' }}>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>Subject</label>
                        <select
                            value={selectedSubject}
                            onChange={(e) => setSelectedSubject(e.target.value)}
                            disabled={!selectedLevel}
                            style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #ddd6fe', borderRadius: '0.375rem', backgroundColor: '#ffffff' }}
                        >
                            <option value="">All Subjects</option>
                            {allTags
                            .filter((tag) => tag.level === selectedLevel && tag.tag_type === 'SUBJECT')
                            .map((tag) => (
                                <option key={tag.name} value={tag.name}>
                                {tag.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div style={{ flex: '1 1 150px' }}>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>Exam</label>
                        <select
                            value={selectedExam}
                            onChange={(e) => setSelectedExam(e.target.value)}
                            disabled={!selectedLevel}
                            style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #ddd6fe', borderRadius: '0.375rem', backgroundColor: '#ffffff' }}
                        >
                            <option value="">All Exams</option>
                            {allTags
                            .filter((tag) => tag.level === selectedLevel && tag.tag_type === 'EXAM')
                            .map((tag) => (
                                <option key={tag.name} value={tag.name}>
                                {tag.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div style={{ flex: '1 1 150px' }}>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>Topic</label>
                        <select
                            value={selectedTopic}
                            onChange={(e) => setSelectedTopic(e.target.value)}
                            disabled={!selectedLevel}
                            style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #ddd6fe', borderRadius: '0.375rem', backgroundColor: '#ffffff' }}
                        >
                            <option value="">All Topics</option>
                            {allTags
                            .filter((tag) => tag.level === selectedLevel && tag.tag_type === 'TOPIC')
                            .map((tag) => (
                                <option key={tag.name} value={tag.name}>
                                {tag.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div style={{ flex: '1 1 150px' }}>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>Difficulty</label>
                        <select
                            value={selectedDifficulty}
                            onChange={(e) => setSelectedDifficulty(e.target.value)}
                            style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #ddd6fe', borderRadius: '0.375rem', backgroundColor: '#ffffff' }}
                        >
                            <option value="">All Difficulties</option>
                            {QUESTIONDIFFICULTY.map(diff => (
                                <option key={diff} value={diff}>{diff}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Questions Grid Table */}
            <div style={{ height: 'auto', maxHeight: 'calc(100vh - 250px)', overflowY: 'auto', overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '0.5rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ backgroundColor: '#f3f4f6', position: 'sticky', top: 0, zIndex: 5 }}>
                        <tr>
                            <th style={{ padding: '0.75rem', border: '1px solid #e5e7eb', textAlign: 'center', width: '3%' }}>Select</th>
                            <th style={{ padding: '0.75rem', border: '1px solid #e5e7eb', textAlign: 'left', width: '40%' }}>Question Text</th>
                            <th style={{ padding: '0.75rem', border: '1px solid #e5e7eb', textAlign: 'left', width: '10%' }}>Level</th>
                            <th style={{ padding: '0.75rem', border: '1px solid #e5e7eb', textAlign: 'left', width: '10%' }}>Subject</th>
                            <th style={{ padding: '0.75rem', border: '1px solid #e5e7eb', textAlign: 'left', width: '10%' }}>Difficulty</th>
                            {showCorrectOption && <th style={{ padding: '0.75rem', border: '1px solid #e5e7eb', textAlign: 'left', width: '10%' }}>Correct Answer</th>}
                            {showUserResult && <th style={{ padding: '0.75rem', border: '1px solid #e5e7eb', textAlign: 'left', width: '10%' }}>Your Answer</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredAndPaginatedQuestions.length > 0 ? (
                            filteredAndPaginatedQuestions.map(question => (
                                <tr key={question.question_id} style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#ffffff' }}>
                                    <td style={{ padding: '0.75rem', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                                        {/* <Checkbox
                                            checked={selectedRows.includes(question.id)}
                                            onCheckedChange={(checked) => handleCheckboxChange(question.id, !!checked)}
                                        /> */}
                                        <Checkbox checked={selectedRows.includes(question.question_id)} onCheckedChange={(e) => handleCheckboxChange(question.question_id, e)} />
                                    </td>
                                    <td style={{ padding: '0.75rem', border: '1px solid #e5e7eb' }}>{question.question_text}</td>
                                    <td style={{ padding: '0.75rem', border: '1px solid #e5e7eb' }}>{question.level}</td>
                                    <td style={{ padding: '0.75rem', border: '1px solid #e5e7eb' }}>{question.subject}</td>
                                    <td style={{ padding: '0.75rem', border: '1px solid #e5e7eb' }}>{question.difficulty}</td>
                                    {showCorrectOption && <td style={{ padding: '0.75rem', border: '1px solid #e5e7eb' }}>{getAnswerFromCorrectOption(question, question.correct_option)}</td>}
                                    {showUserResult && <td style={{ padding: '0.75rem', border: '1px solid #e5e7eb' }}>{getAnswerFromCorrectOption(question, question.user_selected_option)}</td>}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={showCorrectOption && showUserResult ? 7 : (showCorrectOption || showUserResult ? 6 : 5)} style={{ padding: '1rem', textAlign: 'center', color: '#6b7280' }}>No favorite questions found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Footer */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', padding: '0.5rem 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <label htmlFor="pageSize" style={{ fontSize: '0.875rem', color: '#4b5563' }}>Items per page:</label>
                    <select
                        id="pageSize"
                        value={pageSize}
                        onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                        style={{ padding: '0.3rem 0.5rem', borderRadius: '0.375rem', border: '1px solid #ddd6fe', backgroundColor: '#ffffff' }}
                    >
                        {itemsPerPageOptions.map(size => (
                            <option key={size} value={size}>{size}</option>
                        ))}
                        {/* <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option> */}
                    </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button
                        onClick={() => handlePageChange('prev')}
                        style={{ padding: '0.5rem 1rem', borderRadius: '0.375rem', border: '1px solid #ddd6fe', backgroundColor: '#ffffff', cursor: 'pointer', fontSize: '0.875rem', color: '#374151' }}
                    >
                        Previous
                    </button>
                    <span style={{ fontSize: '0.875rem', color: '#4b5563' }}>Page {currentPage} of {totalPages === 0 ? 1 : totalPages} · Total: {totalFilteredQuestions}</span>
                    <button
                        onClick={() => handlePageChange('next')}
                        style={{ padding: '0.5rem 1rem', borderRadius: '0.375rem', border: '1px solid #ddd6fe', backgroundColor: '#ffffff', cursor: 'pointer', fontSize: '0.875rem', color: '#374151' }}
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FavoriteQuestionsGrid;
